/**
 * PanelMapFeaturesService — 순수 Feature 생성·좌표 변환 함수
 *
 * Vue 의존성 없음. vectorSource/map 등은 호출자가 주입.
 * panelMapFeaturesMixin.js 에서 import 하여 thin wrapper 로 사용.
 */
import { Polygon } from 'ol/geom';
import Feature from 'ol/Feature';
import { closeRingCoordinates } from '../utils/closeRingCoordinates.js';
import { toPanelMapFeatureId } from '../utils/panelMapFeatureId.js';
import {
  buildModuleMapFeatureProps,
  buildStringMapFeatureProps
} from '../utils/panelMapFeatureProps.js';
import { moduleFromApi, stringFromApi } from '../utils/panelHierarchyMappers.js';
import { getQuadCorners } from '../utils/getModuleCellCorners.js';
import { getWidthOrHeight } from '../utils/panelEdgeOrientation.js';

// ────────────────────────────────────────────────────────────
//  좌표 유틸리티
// ────────────────────────────────────────────────────────────

/**
 * shapeGeog.coordinates → 단일 링 좌표 배열
 *
 * GeoJSON(중첩 링) 구조와 플랫 링 구조를 모두 지원한다.
 * @param {{ coordinates: number[][] | number[][][] }} shapeGeog
 * @returns {number[][] | null}
 */
export function getShapeGeogPolygonRing(shapeGeog) {
  const coords = shapeGeog?.coordinates;
  if (!coords?.length) {
    return null;
  }
  const first = coords[0];
  if (Array.isArray(first) && typeof first[0] === 'number') {
    return coords.map(c => [...c]);
  }
  if (Array.isArray(first) && Array.isArray(first[0])) {
    return first.map(c => [...c]);
  }
  return null;
}

/**
 * 맵 좌표(EPSG:3857 등) → API shapeCoordinates (EPSG:4326 또는 호환)
 *
 * closeRingCoordinates 로 링을 닫아 GeoLatte Polygon(LinearRing) 요구를 충족한다.
 * @param {number[][]} coordinates
 * @returns {number[][]}
 */
export function convToWGS84(coordinates) {
  if (!coordinates?.length) {
    return [];
  }
  return closeRingCoordinates(coordinates.map(coord => [...coord]));
}

// ────────────────────────────────────────────────────────────
//  Feature 생성
// ────────────────────────────────────────────────────────────

/**
 * 좌표·계층 정보로 OpenLayers Polygon Feature 생성
 *
 * @param {string|number} id
 * @param {string} name
 * @param {number[][]} coordinates
 * @param {import('ol/style/Style').StyleLike|null} style
 * @param {{
 *   solarSectionId?: number|string,
 *   solarArrayId?: number|string,
 *   solarStringId?: number|string,
 *   solarStringSeq?: number|string,
 *   solarModuleId?: number|string,
 *   featureType?: string
 * }} hierarchy
 * @returns {Feature}
 * @throws {Error} 좌표가 유효하지 않을 때
 */
export function convToFeatureWithHierarchy(id, name, coordinates, style, hierarchy) {
  const webCoordinates = closeRingCoordinates(
    Array.isArray(coordinates?.[0]) ? coordinates.map(c => [...c]) : coordinates
  );
  if (!webCoordinates?.length || webCoordinates.length < 4) {
    throw new Error(`Invalid coordinates for feature ${id}`);
  }

  const isModule = Boolean(hierarchy.solarModuleId ?? hierarchy.moduleDisplayNo);
  const stringDomain = stringFromApi({
    id: hierarchy.solarStringSeq ?? hierarchy.stringSeq,
    stringId: hierarchy.solarStringId ?? hierarchy.stringDisplayNo
  });
  const moduleDomain = isModule
    ? moduleFromApi({
        id: hierarchy.moduleSeq ?? hierarchy.solarModuleSeq,
        moduleId: hierarchy.solarModuleId ?? hierarchy.moduleDisplayNo
      })
    : null;

  const identityProps = isModule
    ? buildModuleMapFeatureProps(moduleDomain, stringDomain, {
        sectionSeq: hierarchy.solarSectionId,
        arraySeq: hierarchy.solarArrayId ?? hierarchy.arraySeq
      })
    : buildStringMapFeatureProps(stringDomain, {
        sectionSeq: hierarchy.solarSectionId,
        arraySeq: hierarchy.solarArrayId ?? hierarchy.arraySeq
      });

  const feature = new Feature({
    geometry: new Polygon([webCoordinates]),
    name,
    ...identityProps,
    featureType:
      hierarchy.featureType ||
      identityProps.featureType ||
      (isModule ? 'Module' : 'String')
  });
  const featureType = feature.get('featureType');
  feature.setId(
    toPanelMapFeatureId(id, featureType, {
      solarModuleId: hierarchy.solarModuleId
    })
  );
  if (style) {
    feature.setStyle(style);
  }
  return feature;
}

// ────────────────────────────────────────────────────────────
//  모듈 크기 산출
// ────────────────────────────────────────────────────────────

/**
 * String 의 shapeGeog·rowNum·colNum 으로 모듈 1칸 크기를 산출한다.
 *
 * @param {{ shapeGeog?: object, colNum?: number, rowNum?: number }} solarString
 * @returns {{ moduleWidth: number, moduleHeight: number } | null}
 *   계산 성공 시 {moduleWidth, moduleHeight} 객체, 실패 시 null.
 *   한 방향만 계산된 경우 반대 방향은 undefined.
 */
export function computeModuleDimensions(solarString) {
  try {
    const ring = getShapeGeogPolygonRing(solarString?.shapeGeog);
    const corners = ring ? getQuadCorners(ring) : [];
    const colNum = solarString?.colNum;
    const rowNum = solarString?.rowNum;
    if (corners.length < 4 || !colNum || !rowNum) {
      return null;
    }
    const [c0, c1, c2] = corners;
    const oneSide = getWidthOrHeight(c0, c1);
    const otherSide = getWidthOrHeight(c1, c2);

    const result = {};
    if (oneSide.type === 'width') {
      result.moduleWidth = oneSide.distance / colNum;
    } else {
      result.moduleHeight = oneSide.distance / rowNum;
    }
    if (otherSide.type === 'width') {
      result.moduleWidth = otherSide.distance / colNum;
    } else {
      result.moduleHeight = otherSide.distance / rowNum;
    }
    return result;
  } catch (error) {
    console.error('Error computing module dimensions:', error);
    return null;
  }
}

// ────────────────────────────────────────────────────────────
//  참조 String 선택
// ────────────────────────────────────────────────────────────

/**
 * rowNum/colNum·shapeGeog 를 가진 String 중 stringId 가 가장 작은 것을 찾는다.
 *
 * @param {Array<{ stringId?: number|string, id?: number|string, shapeGeog?: object, rowNum?: number, colNum?: number }>} strings
 * @returns {object | null} 가장 작은 stringId 의 String 객체, 없으면 null
 */
export function findBestReferenceString(strings) {
  if (!Array.isArray(strings) || !strings.length) {
    return null;
  }
  let best = null;
  let bestId = Infinity;
  for (const s of strings) {
    const id = parseInt(s?.stringId ?? s?.id, 10);
    if (!s?.shapeGeog || !s?.rowNum || !s?.colNum) {
      continue;
    }
    if (!Number.isFinite(id) || id >= bestId) {
      continue;
    }
    bestId = id;
    best = s;
  }
  return best;
}
